import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { useFormContext } from 'react-hook-form'

import { Image } from '@tribeplatform/gql-client/types'
import { useImageUploader } from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import {
  FormControl,
  FormControlProps,
} from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/index.js'
import { handleUploadFileError } from '../utils/error.js'

export type AvatarInputProps = {
  name: string
  alt?: string
  picture: Image
} & FormControlProps

export const AvatarInput = (props: AvatarInputProps) => {
  const { name, picture, alt, label, helperText, error, invalid } = props

  const { setValue, register } = useFormContext()
  const fileInputRef = useRef<HTMLInputElement>()
  const { uploadImage, loadingImages, isLoading } = useImageUploader()
  const intl = useI18n()
  const [uploadedFile, setUploadedFile] = useState<Image>()

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const [file] = e.target.files

    if (!file) {
      return
    }

    try {
      const uploadedImage = await uploadImage(file)
      setUploadedFile(uploadedImage)
      setValue(name, uploadedImage.id)
    } catch (error) {
      handleUploadFileError(intl)(error)
    }
  }

  const avatar = loadingImages.at(0) ?? uploadedFile ?? picture
  useEffect(() => {
    register(name)
  }, [name, register])

  return (
    <div>
      {label ? (
        <FormControl.Label htmlFor={name}>{label}</FormControl.Label>
      ) : null}
      <div
        className={clsx(
          'hover:bg-surface-hovered rounded-avatar relative w-32 h-32',
          label && 'mt-1',
        )}
      >
        <Avatar size="8x" name={alt} src={avatar?.urls?.small || avatar?.url} />
        <input
          type="file"
          className="hidden"
          multiple={false}
          ref={fileInputRef}
          onChange={onChangeFile}
          accept="image/*"
        />
        {isLoading && (
          <div className="flex items-center justify-center text-white rounded-avatar bg-background-backdrop absolute top-0 bottom-0 start-0 end-0">
            <SvgIcon className="animate-spin h-7 w-7" name="spinner" />
          </div>
        )}
        {!isLoading && (
          <div
            onClick={() => {
              fileInputRef.current.click()
            }}
            className="flex items-center justify-center rounded-avatar text-white bg-black bg-opacity-50 opacity-0 hover:opacity-100 absolute top-0 bottom-0 start-0 end-0 cursor-pointer"
          >
            <T id="Generics.Change.Verb" defaultMessage="Change" />
          </div>
        )}
      </div>
      <FormControl.Meta {...{ helperText, error, invalid }} />
    </div>
  )
}
